import Phaser from "phaser";
import { GameConfig } from "@/config/GameConfig";
import type { LevelDefinition } from "@/levels/types";
import type { WorldDefinition } from "@/levels/worldTypes";
import { LevelRegistry } from "@/levels/LevelRegistry";
import { WorldRegistry } from "@/levels/WorldRegistry";
import { ProgressStorage } from "@/progress/ProgressStorage";
import { ScoreV2Storage } from "@/progress/ScoreV2Storage";
import { isQaCampaignUnlockActive, isSelectableForQa } from "@/qa/QaMode";
import { createButton } from "@/ui/Button";
import { drawStar } from "@/ui/Star";
import { drawWorldIcon } from "@/ui/worldGraphics";
import { hexToCss } from "@/ui/colors";
import { attachOrientationGate, destroyLayoutChildren, type OrientationGateHandle } from "@/scenes/OrientationGate";

interface LevelSelectSceneData {
  worldId?: string;
}

const TILE_SIZE = 120;
const TILE_GAP = 16;
const TOP_PADDING = 8;
const SIDE_PADDING = 20;

/**
 * Shows every level of one world as a scrollable grid of tiles. Which
 * world is `worldId` in the scene data (defaults to the first world if
 * missing/invalid, so a stray direct navigation never crashes); bounces
 * straight back to World Select if that world turns out to be locked.
 */
export class LevelSelectScene extends Phaser.Scene {
  private world!: WorldDefinition;
  /**
   * `?qa=unlock-campaign` on an allowed QA host - see `isQaCampaignUnlockActive()`.
   * Presentation-only: read once per Scene load, never written back
   * anywhere, never touches `ProgressStorage`.
   */
  private qaUnlockAll = false;
  private contentContainer!: Phaser.GameObjects.Container;
  private viewportTop = 0;
  private viewportBottom = 0;
  private scrollY = 0;
  private maxScroll = 0;
  private dragging = false;
  private dragStartPointerY = 0;
  private dragStartScroll = 0;
  private tiles: { container: Phaser.GameObjects.Container; onClick: () => void }[] = [];
  /** PR31 follow-up fix: see `OrientationGate.ts`'s own doc. No `requiresExplicitResume` - this Scene has no run/state to protect, so rotating back to portrait restores interaction immediately. */
  private orientationGate!: OrientationGateHandle;

  constructor() {
    super("LevelSelectScene");
  }

  create(data: LevelSelectSceneData): void {
    this.qaUnlockAll = isQaCampaignUnlockActive(window.location.hostname, window.location.search);
    const requested = data.worldId ? WorldRegistry.byId(data.worldId) : undefined;
    this.world = requested ?? WorldRegistry.all()[0];

    if (!isSelectableForQa(ProgressStorage.isWorldUnlocked(this.world.id), this.qaUnlockAll)) {
      this.scene.start("WorldSelectScene");
      return;
    }

    this.cameras.main.setBackgroundColor(GameConfig.colors.background);
    this.scrollY = 0;
    this.buildLayout();
    this.scale.on("resize", this.onResize, this);
    this.input.on("pointermove", this.onPointerMove, this);
    this.input.on("pointerup", this.onPointerUp, this);
    this.input.on("pointerupoutside", this.onPointerUp, this);
    this.input.on("wheel", this.onWheel, this);
    this.events.once("shutdown", () => this.teardown());
    this.events.once("destroy", () => this.teardown());
    this.orientationGate = attachOrientationGate(this);
    this.orientationGate.check();
  }

  private teardown(): void {
    this.scale.off("resize", this.onResize, this);
    this.input.off("pointermove", this.onPointerMove, this);
    this.input.off("pointerup", this.onPointerUp, this);
    this.input.off("pointerupoutside", this.onPointerUp, this);
    this.input.off("wheel", this.onWheel, this);
  }

  private onResize(): void {
    this.scrollY = 0;
    this.buildLayout();
  }

  private buildLayout(): void {
    destroyLayoutChildren(this);
    this.tiles = [];
    const { width, height } = this.scale;
    const centerX = width / 2;
    const theme = this.world.theme;

    const titleSize = Math.max(26, Math.min(40, width * 0.065));
    const title = this.add
      .text(centerX, height * 0.08, this.world.name, {
        fontFamily: "Arial, sans-serif",
        fontSize: `${titleSize}px`,
        fontStyle: "bold",
        color: hexToCss(theme.accent),
      })
      .setOrigin(0.5);

    // A small world icon to the left of the name - the same visual
    // language as World Select and the in-game HUD, never covering the
    // name/stats/tiles.
    const iconRadius = titleSize * 0.42;
    const iconGraphics = this.add.graphics();
    drawWorldIcon(iconGraphics, theme, title.x - title.width / 2 - iconRadius - 10, title.y, iconRadius);

    const subtitle = this.add
      .text(centerX, title.y + titleSize * 0.7, "LEVELS", {
        fontFamily: "Arial, sans-serif",
        fontSize: "14px",
        color: "#8b93a7",
      })
      .setOrigin(0.5);

    createButton(this, 64, height * 0.08, "BACK", () => this.scene.start("WorldSelectScene"), {
      width: 96,
      height: 40,
      fontSize: "15px",
      fillColor: 0x1c2030,
      hoverColor: 0x262c40,
      textColor: "#f5f7fa",
    });

    this.viewportTop = subtitle.y + 26;
    this.viewportBottom = height - 16;
    const viewportHeight = Math.max(1, this.viewportBottom - this.viewportTop);

    // Background zone: added first so tiles (added after) win hit-testing
    // priority, and dragging on empty space still scrolls the list.
    const dragZone = this.add
      .zone(0, this.viewportTop, width, viewportHeight)
      .setOrigin(0, 0)
      .setInteractive();
    dragZone.on("pointerdown", (pointer: Phaser.Input.Pointer) => {
      this.dragging = true;
      this.dragStartPointerY = pointer.y;
      this.dragStartScroll = this.scrollY;
    });

    this.contentContainer = this.add.container(0, this.viewportTop);

    const levels = LevelRegistry.worlds().find((g) => g.world.id === this.world.id)?.levels ?? [];
    const columns = Math.max(1, Math.min(5, Math.floor((width - SIDE_PADDING * 2) / (TILE_SIZE + TILE_GAP))));
    const gridWidth = columns * TILE_SIZE + (columns - 1) * TILE_GAP;
    const gridStartX = centerX - gridWidth / 2;

    levels.forEach((level, index) => {
      const col = index % columns;
      const row = Math.floor(index / columns);
      const tileX = gridStartX + col * (TILE_SIZE + TILE_GAP) + TILE_SIZE / 2;
      const tileY = TOP_PADDING + row * (TILE_SIZE + TILE_GAP) + TILE_SIZE / 2;
      this.createLevelTile(level, tileX, tileY);
    });

    const rows = Math.ceil(levels.length / columns);
    const contentHeight = TOP_PADDING + rows * (TILE_SIZE + TILE_GAP);
    this.maxScroll = Math.max(0, contentHeight - viewportHeight);
    this.scrollY = Math.min(this.scrollY, this.maxScroll);
    this.contentContainer.y = this.viewportTop - this.scrollY;

    const maskShape = this.make.graphics({});
    maskShape.fillRect(0, this.viewportTop, width, viewportHeight);
    this.contentContainer.setMask(maskShape.createGeometryMask());
  }

  private createLevelTile(level: LevelDefinition, x: number, y: number): void {
    // Presentation-only: `completed`/`stats` below are always the real,
    // unmodified `ProgressStorage` values - only this gating decision is
    // affected by the QA override.
    const unlocked = isSelectableForQa(ProgressStorage.isUnlocked(level), this.qaUnlockAll);
    const completed = ProgressStorage.isCompleted(level);
    const stats = ProgressStorage.getLevelStats(level);
    const theme = this.world.theme;

    const container = this.add.container(x, y);
    const graphics = this.add.graphics();

    const fillColor = completed ? 0x1d3b2c : unlocked ? theme.primary : 0x14161f;
    const borderColor = completed ? GameConfig.colors.gateOpen : unlocked ? theme.accent : GameConfig.colors.gridLine;

    graphics.fillStyle(fillColor, 1);
    graphics.fillRoundedRect(-TILE_SIZE / 2, -TILE_SIZE / 2, TILE_SIZE, TILE_SIZE, 14);
    graphics.lineStyle(unlocked ? 2 : 1.5, borderColor, unlocked ? 1 : 0.6);
    graphics.strokeRoundedRect(-TILE_SIZE / 2, -TILE_SIZE / 2, TILE_SIZE, TILE_SIZE, 14);
    container.add(graphics);

    const numberText = this.add
      .text(0, -38, `${level.order}`, {
        fontFamily: "Arial, sans-serif",
        fontSize: "24px",
        fontStyle: "bold",
        color: unlocked ? "#f5f7fa" : "#4a5168",
      })
      .setOrigin(0.5);
    container.add(numberText);

    const nameText = this.add
      .text(0, -12, level.name, {
        fontFamily: "Arial, sans-serif",
        fontSize: "10px",
        color: unlocked ? "#8b93a7" : "#4a5168",
        align: "center",
        wordWrap: { width: TILE_SIZE - 12 },
      })
      .setOrigin(0.5, 0);
    container.add(nameText);

    if (unlocked) {
      const starsGraphics = this.add.graphics();
      for (let i = 0; i < 3; i++) {
        const starX = (i - 1) * 15;
        drawStar(starsGraphics, starX, 20, 6, i < stats.stars ? GameConfig.colors.star : GameConfig.colors.starEmpty);
      }
      container.add(starsGraphics);

      // Player-facing "Best" is the Score & Stars V2 Final Score record
      // (ScoreV2Storage) - never the legacy Bite-Score-only number, which
      // is preserved internally (see `LevelStats.highScore`'s doc) but no
      // longer presented as the competitive best. A level can be
      // completed/starred with no Score V2 record yet (e.g. it was only
      // ever completed before Score V2 existed) - shown honestly as "—",
      // never mislabeling the legacy number as a Final Score.
      const bestFinalScoreText = this.add
        .text(0, 32, ScoreV2Storage.hasBestFinalScore(level) ? `Best ${ScoreV2Storage.getBestFinalScore(level)}` : "—", {
          fontFamily: "Arial, sans-serif",
          fontSize: "10px",
          color: "#8b93a7",
        })
        .setOrigin(0.5, 0);
      container.add(bestFinalScoreText);
    }

    if (!unlocked) {
      this.drawLockIcon(container, TILE_SIZE / 2 - 20, -TILE_SIZE / 2 + 20);
    }
    if (completed) {
      this.drawCheckBadge(container, TILE_SIZE / 2 - 18, -TILE_SIZE / 2 + 18);
    }

    if (unlocked) {
      const hitArea = new Phaser.Geom.Rectangle(-TILE_SIZE / 2, -TILE_SIZE / 2, TILE_SIZE, TILE_SIZE);
      graphics.setInteractive({ hitArea, hitAreaCallback: Phaser.Geom.Rectangle.Contains, useHandCursor: true });
      const onClick = () => {
        if (!this.isTileVisible(container)) return;
        this.scene.start("GameScene", { levelId: level.id, runSource: "level_select" });
      };
      graphics.on("pointerdown", () => {
        this.tweens.add({ targets: container, scale: 0.94, duration: 80, yoyo: true, onComplete: onClick });
      });
      this.tiles.push({ container, onClick });
    }

    this.contentContainer.add(container);
  }

  private isTileVisible(tile: Phaser.GameObjects.Container): boolean {
    const worldY = this.contentContainer.y + tile.y;
    const half = TILE_SIZE / 2;
    return worldY + half >= this.viewportTop && worldY - half <= this.viewportBottom;
  }

  private drawLockIcon(container: Phaser.GameObjects.Container, x: number, y: number): void {
    const g = this.add.graphics();
    g.lineStyle(2, 0x8b93a7, 1);
    g.strokeRoundedRect(x - 6, y - 2, 12, 10, 2);
    g.beginPath();
    g.arc(x, y - 2, 5, Math.PI, Math.PI * 2, false);
    g.strokePath();
    container.add(g);
  }

  private drawCheckBadge(container: Phaser.GameObjects.Container, x: number, y: number): void {
    const g = this.add.graphics();
    g.fillStyle(GameConfig.colors.gateOpen, 1);
    g.fillCircle(x, y, 10);
    g.lineStyle(2.5, 0x0f1117, 1);
    g.beginPath();
    g.moveTo(x - 4, y);
    g.lineTo(x - 1, y + 3.5);
    g.lineTo(x + 4.5, y - 4);
    g.strokePath();
    container.add(g);
  }

  private onPointerMove(pointer: Phaser.Input.Pointer): void {
    if (!this.dragging || this.maxScroll <= 0) return;
    const delta = pointer.y - this.dragStartPointerY;
    this.scrollY = Phaser.Math.Clamp(this.dragStartScroll - delta, 0, this.maxScroll);
    this.contentContainer.y = this.viewportTop - this.scrollY;
  }

  private onPointerUp(): void {
    this.dragging = false;
  }

  private onWheel(_pointer: Phaser.Input.Pointer, _objects: unknown, _dx: number, dy: number): void {
    if (this.maxScroll <= 0) return;
    this.scrollY = Phaser.Math.Clamp(this.scrollY + dy * 0.5, 0, this.maxScroll);
    this.contentContainer.y = this.viewportTop - this.scrollY;
  }
}
